// ระบบจัดการ Role และ Permission สำหรับระบบ CannaTrade
// รองรับ roles: admin, customer, momo (manager), girl (สำหรับระบบจองเด็กเอ็น)
// และ roles ใหม่: user, farm (สำหรับระบบตลาดค้าส่ง)

import { doc, setDoc, updateDoc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { JOB_ROLES } from '../data/nurseAppModels';

// กำหนด roles และ permissions
export const ROLES = {
  // Roles เดิม (ระบบจองเด็กเอ็น)
  ADMIN: 'admin',
  CUSTOMER: 'customer', 
  MOMO: 'momo', // Manager/โม
  GIRL: 'girl',
  
  // Roles ใหม่ (ระบบตลาดค้าส่ง) - Version 2
  USER: 'user', // ผู้ใช้ทั่วไป (ซื้อและขายได้)
  FARM: 'farm'  // ฟาร์ม (ซื้อและขายได้ + Farm Dashboard)
};

// กำหนด permissions สำหรับแต่ละ role
export const PERMISSIONS = {
  // Admin permissions
  ADMIN: {
    manageBookings: true,
    manageRevenue: true,
    manageCoins: true,
    manageUsers: true,
    manageGirls: true,
    manageMomo: true,
    viewAllData: true,
    editSystemSettings: true
  },
  
  // Customer permissions
  CUSTOMER: {
    viewOwnBookings: true,
    createBooking: true,
    viewGirlProfiles: true,
    manageOwnProfile: true
  },
  
  // Momo (Manager) permissions
  MOMO: {
    manageOwnGirls: true, // จัดการน้องในสังกัดตัวเอง
    viewOwnGirlBookings: true,
    manageOwnGirlSchedules: true,
    viewOwnGirlRevenue: true,
    manageOwnProfile: true
  },
  
  // Girl permissions
  GIRL: {
    manageOwnProfile: true,
    manageOwnSchedule: true,
    viewOwnBookings: true,
    viewOwnRevenue: true,
    updateAvailability: true
  },
  
  // User permissions (ระบบตลาดค้าส่ง) - ผู้ใช้ทั่วไป
  USER: {
    manageOwnStore: true,           // จัดการร้านตัวเอง
    manageOwnProducts: true,         // ลงสินค้า
    purchaseProducts: true,          // ซื้อสินค้า
    createAuction: true,            // สร้างประมูล
    manageOwnAuctions: true,        // จัดการประมูลตัวเอง
    participateInAuction: true,      // เข้าร่วมประมูล
    createBuyerRequest: true,        // สร้างประกาศหาสินค้า
    manageOwnOrders: true,           // ดูออเดอร์
    viewOwnRevenue: true,            // ดูรายได้
    manageOwnProfile: true           // จัดการโปรไฟล์
  },
  
  // Farm permissions (ระบบตลาดค้าส่ง) - ฟาร์ม
  FARM: {
    // ทุกอย่างของ USER
    manageOwnStore: true,
    manageOwnProducts: true,
    purchaseProducts: true,
    createAuction: true,
    manageOwnAuctions: true,
    participateInAuction: true,
    createBuyerRequest: true,
    manageOwnOrders: true,
    viewOwnRevenue: true,
    manageOwnProfile: true,
    
    // Farm Specific Features
    accessFarmDashboard: true,       // เข้าถึง Farm Dashboard
    manageFarmInfo: true,            // จัดการข้อมูล farm
    manageGreenhouses: true,         // จัดการโรงเรือน
    manageGrowingMethods: true,      // จัดการรูปแบบการปลูก
    manageStrains: true,             // จัดการสายพันธุ์
    manageGrowingCycles: true,       // บันทึกรอบการปลูก
    viewGrowingCycles: true          // ดูรอบการปลูก
  }
};

/**
 * ตรวจสอบว่า user มี permission หรือไม่
 * @param {object} user - ข้อมูล user
 * @param {string} permission - permission ที่ต้องการตรวจสอบ
 * @returns {boolean}
 */
export const hasPermission = (user, permission) => {
  if (!user || !user.role) return false;
  
  const rolePermissions = PERMISSIONS[user.role.toUpperCase()];
  return rolePermissions && rolePermissions[permission] === true;
};

/**
 * ตรวจสอบว่า user เป็น role ที่กำหนดหรือไม่
 * @param {object} user - ข้อมูล user
 * @param {string} role - role ที่ต้องการตรวจสอบ
 * @returns {boolean}
 */
export const hasRole = (user, role) => {
  return user && user.role === role;
};

/**
 * ตรวจสอบว่า user เป็น admin หรือไม่
 * @param {object} user - ข้อมูล user
 * @returns {boolean}
 */
export const isAdmin = (user) => {
  return hasRole(user, ROLES.ADMIN);
};

/**
 * ตรวจสอบว่า user เป็น momo หรือไม่
 * @param {object} user - ข้อมูล user
 * @returns {boolean}
 */
export const isMomo = (user) => {
  return hasRole(user, ROLES.MOMO);
};

/**
 * ตรวจสอบว่า user เป็น girl หรือไม่
 * @param {object} user - ข้อมูล user
 * @returns {boolean}
 */
export const isGirl = (user) => {
  return hasRole(user, ROLES.GIRL);
};

/**
 * ตรวจสอบว่า user เป็น customer หรือไม่
 * @param {object} user - ข้อมูล user
 * @returns {boolean}
 */
export const isCustomer = (user) => {
  return hasRole(user, ROLES.CUSTOMER);
};

/**
 * ตรวจสอบว่า user เป็น farm หรือไม่
 * @param {object} user - ข้อมูล user
 * @returns {boolean}
 */
export const isFarm = (user) => {
  return hasRole(user, ROLES.FARM);
};

/**
 * ตรวจสอบว่า user เป็น marketplace user หรือไม่ (USER, FARM)
 * @param {object} user - ข้อมูล user
 * @returns {boolean}
 */
export const isMarketplaceUser = (user) => {
  if (!user || !user.role) return false;
  return [ROLES.USER, ROLES.FARM].includes(user.role);
};

/**
 * ตรวจสอบว่า user สามารถเข้าถึง Farm Dashboard ได้หรือไม่
 * @param {object} user - ข้อมูล user
 * @returns {boolean}
 */
export const canAccessFarmDashboard = (user) => {
  return isFarm(user);
};

/**
 * ตรวจสอบว่า user สามารถเปิดร้านได้หรือไม่ (ทุกคนใน Marketplace)
 * @param {object} user - ข้อมูล user
 * @returns {boolean}
 * @deprecated - ทุกคนสามารถเปิดร้านได้แล้ว (เปิดอัตโนมัติตอน register)
 */
export const canOpenStore = (user) => {
  if (!user || !user.role) return false;
  return isMarketplaceUser(user); // USER และ FARM เปิดร้านได้
};

/**
 * ตรวจสอบว่า user สามารถซื้อสินค้าได้หรือไม่ (ทุกคนใน Marketplace)
 * @param {object} user - ข้อมูล user
 * @returns {boolean}
 * @deprecated - ทุกคนสามารถซื้อสินค้าได้แล้ว
 */
export const canPurchase = (user) => {
  if (!user || !user.role) return false;
  return isMarketplaceUser(user); // USER และ FARM ซื้อได้
};

/**
 * ตั้งค่า role ให้ user
 * @param {string} userId - ID ของ user
 * @param {string} role - role ที่ต้องการตั้ง
 * @param {object} additionalData - ข้อมูลเพิ่มเติม
 * @returns {Promise<boolean>}
 */
export const setUserRole = async (userId, role, additionalData = {}) => {
  try {
    const userRef = doc(db, 'users', userId);
    
    // ดึงข้อมูล user ปัจจุบัน
    const userSnap = await getDoc(userRef);
    const currentUserData = userSnap.exists() ? userSnap.data() : {};
    
    const roleData = {
      role: role,
      roleUpdatedAt: new Date(),
      email: additionalData.email || currentUserData.email || '',
      displayName: additionalData.displayName || currentUserData.displayName || '',
      createdAt: currentUserData.createdAt || new Date(),
      coins: currentUserData.coins || (role === ROLES.CUSTOMER ? 100 : 0), // แจกเหรียญให้ลูกค้าใหม่
      ...additionalData
    };
    
    // เพิ่มข้อมูลเฉพาะ role
    if (role === ROLES.ADMIN) {
      roleData.isAdmin = true;
      roleData.adminSince = new Date();
    } else if (role === ROLES.MOMO) {
      roleData.isMomo = true;
      roleData.momoSince = new Date();
      roleData.managedGirls = additionalData.managedGirls || []; // รายการ ID ของน้องที่ดูแล
    } else if (role === ROLES.GIRL) {
      roleData.isGirl = true;
      roleData.girlSince = new Date();
      roleData.momoId = additionalData.momoId || null; // ID ของโมที่ดูแล
      roleData.availability = additionalData.availability || {
        isAvailable: true,
        schedule: {},
        workingHours: { start: '09:00', end: '22:00' }
      };
      
      // สร้างโปรไฟล์น้องอัตโนมัติ
      try {
        const { autoCreateGirlProfile } = await import('./girlProfileManager');
        await autoCreateGirlProfile(userId, { ...currentUserData, role });
      } catch (profileError) {
        console.error('⚠️ ไม่สามารถสร้างโปรไฟล์น้องอัตโนมัติ:', profileError);
        // ไม่ throw error เพื่อไม่ให้การเปลี่ยน role ล้มเหลว
      }
    } else if (role === ROLES.CUSTOMER) {
      roleData.isCustomer = true;
      roleData.customerSince = new Date();
    } else if (role === ROLES.USER) {
      roleData.isUser = true;
      roleData.userSince = new Date();
      
      // สร้างร้านอัตโนมัติสำหรับ USER
      try {
        await autoCreateStore(userId, roleData, additionalData);
      } catch (storeError) {
        console.error('⚠️ ไม่สามารถสร้างร้านอัตโนมัติ:', storeError);
        // ไม่ throw error เพื่อไม่ให้การเปลี่ยน role ล้มเหลว
      }
    } else if (role === ROLES.FARM) {
      roleData.isFarm = true;
      roleData.farmSince = new Date();
      
      // สร้างร้านอัตโนมัติสำหรับ FARM
      try {
        await autoCreateStore(userId, roleData, additionalData);
      } catch (storeError) {
        console.error('⚠️ ไม่สามารถสร้างร้านอัตโนมัติ:', storeError);
        // ไม่ throw error เพื่อไม่ให้การเปลี่ยน role ล้มเหลว
      }
    } else if (role === JOB_ROLES.NURSE) {
      roleData.jobRole = JOB_ROLES.NURSE;
      roleData.isNurse = true;
      roleData.nurseSince = new Date();
      // ไม่สร้างร้านสำหรับ Nurse App
    } else if (role === JOB_ROLES.ASSISTANT_NURSE) {
      roleData.jobRole = JOB_ROLES.ASSISTANT_NURSE;
      roleData.isAssistantNurse = true;
      roleData.assistantNurseSince = new Date();
      // ไม่สร้างร้านสำหรับ Nurse App
    } else if (role === JOB_ROLES.DOCTOR) {
      roleData.jobRole = JOB_ROLES.DOCTOR;
      roleData.isDoctor = true;
      roleData.doctorSince = new Date();
      // ไม่สร้างร้านสำหรับ Nurse App
    } else if (role === JOB_ROLES.STAFF) {
      roleData.jobRole = JOB_ROLES.STAFF;
      roleData.isStaff = true;
      roleData.staffSince = new Date();
      // ไม่สร้างร้านสำหรับ Nurse App
    }
    
    await setDoc(userRef, roleData, { merge: true });
    console.log(`✅ ตั้งค่า ${role} role ให้ ${userId} สำเร็จ`);
    return true;
  } catch (error) {
    console.error(`❌ ไม่สามารถตั้งค่า ${role} role:`, error);
    return false;
  }
};

/**
 * เพิ่มน้องให้โมดูแล
 * @param {string} momoId - ID ของโม
 * @param {string} girlId - ID ของน้อง
 * @returns {Promise<boolean>}
 */
export const assignGirlToMomo = async (momoId, girlId) => {
  try {
    // อัปเดตข้อมูลโม
    const momoRef = doc(db, 'users', momoId);
    const momoSnap = await getDoc(momoRef);
    
    if (!momoSnap.exists()) {
      throw new Error('ไม่พบโม');
    }
    
    const momoData = momoSnap.data();
    const managedGirls = momoData.managedGirls || [];
    
    if (!managedGirls.includes(girlId)) {
      managedGirls.push(girlId);
      await updateDoc(momoRef, { managedGirls });
    }
    
    // อัปเดตข้อมูลน้อง
    const girlRef = doc(db, 'users', girlId);
    await updateDoc(girlRef, { momoId });
    
    console.log(`✅ เพิ่มน้อง ${girlId} ให้โม ${momoId} สำเร็จ`);
    return true;
  } catch (error) {
    console.error('❌ ไม่สามารถเพิ่มน้องให้โม:', error);
    return false;
  }
};

/**
 * ลบน้องออกจากการดูแลของโม
 * @param {string} momoId - ID ของโม
 * @param {string} girlId - ID ของน้อง
 * @returns {Promise<boolean>}
 */
export const removeGirlFromMomo = async (momoId, girlId) => {
  try {
    // อัปเดตข้อมูลโม
    const momoRef = doc(db, 'users', momoId);
    const momoSnap = await getDoc(momoRef);
    
    if (!momoSnap.exists()) {
      throw new Error('ไม่พบโม');
    }
    
    const momoData = momoSnap.data();
    const managedGirls = momoData.managedGirls || [];
    const updatedGirls = managedGirls.filter(id => id !== girlId);
    
    await updateDoc(momoRef, { managedGirls: updatedGirls });
    
    // อัปเดตข้อมูลน้อง
    const girlRef = doc(db, 'users', girlId);
    await updateDoc(girlRef, { momoId: null });
    
    console.log(`✅ ลบน้อง ${girlId} ออกจากโม ${momoId} สำเร็จ`);
    return true;
  } catch (error) {
    console.error('❌ ไม่สามารถลบน้องออกจากโม:', error);
    return false;
  }
};

/**
 * ดึงรายการน้องที่โมดูแล
 * @param {string} momoId - ID ของโม
 * @returns {Promise<Array>}
 */
export const getMomoManagedGirls = async (momoId) => {
  try {
    const momoRef = doc(db, 'users', momoId);
    const momoSnap = await getDoc(momoRef);
    
    if (!momoSnap.exists()) {
      return [];
    }
    
    const momoData = momoSnap.data();
    const managedGirlIds = momoData.managedGirls || [];
    
    if (managedGirlIds.length === 0) {
      return [];
    }
    
    // ดึงข้อมูลน้องจาก girls collection
    const girlsRef = collection(db, 'girls');
    const girlsQuery = query(girlsRef, where('__name__', 'in', managedGirlIds));
    const girlsSnapshot = await getDocs(girlsQuery);
    
    return girlsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('❌ ไม่สามารถดึงรายการน้องที่โมดูแล:', error);
    return [];
  }
};

/**
 * ดึงข้อมูลโมที่ดูแลน้อง
 * @param {string} girlId - ID ของน้อง
 * @returns {Promise<object|null>}
 */
export const getGirlMomo = async (girlId) => {
  try {
    const girlRef = doc(db, 'users', girlId);
    const girlSnap = await getDoc(girlRef);
    
    if (!girlSnap.exists()) {
      return null;
    }
    
    const girlData = girlSnap.data();
    const momoId = girlData.momoId;
    
    if (!momoId) {
      return null;
    }
    
    const momoRef = doc(db, 'users', momoId);
    const momoSnap = await getDoc(momoRef);
    
    if (!momoSnap.exists()) {
      return null;
    }
    
    return {
      id: momoSnap.id,
      ...momoSnap.data()
    };
  } catch (error) {
    console.error('❌ ไม่สามารถดึงข้อมูลโมที่ดูแลน้อง:', error);
    return null;
  }
};

/**
 * ดึงรายการ users ทั้งหมดตาม role
 * @param {string} role - role ที่ต้องการดึง
 * @returns {Promise<Array>}
 */
export const getUsersByRole = async (role) => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('role', '==', role));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error(`❌ ไม่สามารถดึงรายการ ${role} users:`, error);
    return [];
  }
};

/**
 * ตรวจสอบว่า user สามารถเข้าถึงข้อมูลของคนอื่นได้หรือไม่
 * @param {object} currentUser - user ปัจจุบัน
 * @param {string} targetUserId - ID ของ user เป้าหมาย
 * @returns {Promise<boolean>}
 */
export const canAccessUserData = async (currentUser, targetUserId) => {
  if (!currentUser) return false;
  
  // Admin สามารถเข้าถึงได้ทุกคน
  if (isAdmin(currentUser)) return true;
  
  // สามารถเข้าถึงข้อมูลของตัวเองได้
  if (currentUser.uid === targetUserId) return true;
  
  // Momo สามารถเข้าถึงข้อมูลของน้องที่ดูแลได้
  if (isMomo(currentUser)) {
    const managedGirls = await getMomoManagedGirls(currentUser.uid);
    return managedGirls.some(girl => girl.id === targetUserId);
  }
  
  return false;
};

/**
 * สร้างร้านอัตโนมัติสำหรับ Marketplace User (USER, FARM)
 * @param {string} userId - ID ของ user
 * @param {object} userData - ข้อมูล user
 * @param {object} additionalData - ข้อมูลเพิ่มเติม (storeName, etc.)
 * @returns {Promise<string|null>} - Store ID หรือ null
 */
export const autoCreateStore = async (userId, userData, additionalData = {}) => {
  try {
    // ตรวจสอบว่าเป็น Marketplace role หรือไม่
    if (![ROLES.USER, ROLES.FARM].includes(userData.role)) {
      return null;
    }

    // ตรวจสอบว่ามีร้านอยู่แล้วหรือไม่
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      const existingUserData = userSnap.data();
      if (existingUserData.storeId) {
        console.log('⚠️ User มีร้านอยู่แล้ว:', existingUserData.storeId);
        return existingUserData.storeId;
      }
    }

    // สร้างชื่อร้าน (default หรือจากฟอร์ม)
    const displayName = userData.displayName || userData.email?.split('@')[0] || 'ผู้ใช้';
    const storeName = additionalData.storeName || `ร้านของ ${displayName}`;

    // สร้างร้าน
    const { createStoreModel } = await import('../data/marketplaceModels');
    const { addDoc, collection } = await import('firebase/firestore');
    
    const storeModel = createStoreModel({
      name: storeName,
      description: '',
      ownerId: userId,
      ownerRole: userData.role,
      status: 'active',
      createdAt: new Date(),
      openedAt: new Date()
    });

    const storeRef = await addDoc(collection(db, 'stores'), storeModel);
    
    // อัพเดท user document
    await updateDoc(userRef, {
      storeId: storeRef.id,
      hasStore: true
    });

    console.log(`✅ สร้างร้านอัตโนมัติสำเร็จ: ${storeName} (${storeRef.id})`);
    return storeRef.id;
  } catch (error) {
    console.error('❌ ไม่สามารถสร้างร้านอัตโนมัติ:', error);
    throw error;
  }
};

/**
 * สร้างข้อมูล user ใหม่พร้อม role
 * @param {string} userId - ID ของ user
 * @param {string} email - email ของ user
 * @param {string} displayName - ชื่อแสดงของ user
 * @param {string} role - role ของ user
 * @param {object} additionalData - ข้อมูลเพิ่มเติม
 * @returns {Promise<boolean>}
 */
export const createUserWithRole = async (userId, email, displayName, role, additionalData = {}) => {
  try {
    const userRef = doc(db, 'users', userId);
    
    const userData = {
      email,
      displayName,
      role,
      createdAt: new Date(),
      coins: role === ROLES.CUSTOMER ? 100 : 0, // แจกเหรียญให้ลูกค้าใหม่
      ...additionalData
    };
    
    await setDoc(userRef, userData);
    
    // ตั้งค่า role เฉพาะ (จะสร้างร้านอัตโนมัติถ้าเป็น USER หรือ FARM)
    await setUserRole(userId, role, { ...userData, ...additionalData });
    
    console.log(`✅ สร้าง ${role} user สำเร็จ: ${displayName}`);
    return true;
  } catch (error) {
    console.error(`❌ ไม่สามารถสร้าง ${role} user:`, error);
    return false;
  }
};
